<template>
  <section
    class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-white overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>

    <div class="relative mx-auto w-[90%] px-6 lg:px-10 pt-24 pb-14">
      <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            NexGrid map
          </p>
          <h2 class="mt-2 text-4xl sm:text-5xl font-semibold leading-[0.95] tracking-tight max-w-2xl">
            Explore NexGrid sections
          </h2>
        </div>
        <p class="max-w-sm text-sm text-zinc-600 dark:text-zinc-400">
          A clear overview of every core area in the workspace, from creation tools to management
          and support.
        </p>
      </div>

      <div class="mt-10">
        <div
          class="overflow-hidden rounded-2xl border border-zinc-200/70 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl">
          <div class="flex transition-transform duration-500 ease-out"
            :style="{ transform: `translateX(-${pageIndex * 100}%)` }">
            <div v-for="(page, idx) in pages" :key="'page-' + idx" class="w-full shrink-0 px-4 py-4 sm:px-6 sm:py-6">
              <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" :class="perPage === 1 ? 'grid-cols-1' : ''">
                <article v-for="s in page" :key="s.id"
                  class="group flex flex-col rounded-xl border border-zinc-200/70 dark:border-zinc-800/80 bg-white/80 dark:bg-zinc-900/90 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-zinc-900/60 hover:shadow-[0_22px_60px_rgba(15,23,42,0.45)] dark:hover:border-zinc-100/70">
                  <div class="relative overflow-hidden">
                    <div class="aspect-[16/9] w-full">
                      <div class="flex h-full w-full flex-row">
                        <div v-for="(color, i) in s.palette" :key="color + i" class="flex-1"
                          :style="{ backgroundColor: color }" />
                      </div>
                    </div>

                    <div class="absolute top-3 left-3 flex items-center gap-2">
                      <span
                        class="rounded-full bg-white/95 dark:bg-black/70 px-2.5 py-1 text-[11px] font-medium tracking-wide ring-1 ring-black/10 dark:ring-white/10">
                        {{ s.category }}
                      </span>
                      <span
                        class="hidden sm:inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-zinc-100 ring-1 ring-white/10">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                        {{ s.metaHint }}
                      </span>
                    </div>
                  </div>

                  <div class="flex flex-1 flex-col px-4 pb-4 pt-3 sm:px-4 sm:pb-4">
                    <div class="flex items-start justify-between gap-3">
                      <h3 class="text-sm font-semibold leading-snug text-zinc-900 dark:text-zinc-50 line-clamp-2">
                        {{ s.title }}
                      </h3>
                      <span
                        class="mt-0.5 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500 group-hover:bg-zinc-900 group-hover:text-zinc-50 dark:bg-zinc-800/80 dark:text-zinc-300 dark:group-hover:bg-zinc-50 dark:group-hover:text-zinc-900 transition-colors">
                        {{ s.short }}
                      </span>
                    </div>

                    <p class="mt-1.5 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400 line-clamp-2">
                      {{ s.subtitle }}
                    </p>

                    <div class="mt-3 flex flex-wrap gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                      <span v-for="tag in s.tags" :key="tag"
                        class="rounded-full bg-zinc-900/5 px-2 py-1 dark:bg-zinc-50/5">
                        {{ tag }}
                      </span>
                    </div>

                    <div class="mt-4 flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400">
                      <span class="truncate max-w-[70%]">
                        {{ s.meta }}
                      </span>
                      <span class="inline-flex items-center gap-1">
                        <span class="h-1.5 w-1.5 rounded-full bg-emerald-400/80"></span>
                        <span>Available</span>
                      </span>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div class="hidden sm:flex items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-400/80"></span>
            <span>Mapped to real sections inside NexGrid</span>
          </div>

          <div class="flex items-center gap-3 ml-auto">
            <button @click="prev"
              class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200/80 bg-white/90 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
              aria-label="Previous page">
              ‹
            </button>

            <div class="flex items-center gap-2">
              <button v-for="n in totalPages" :key="'dot-' + n" class="h-2.5 rounded-full transition-all" :class="pageIndex === n - 1
                  ? 'w-6 bg-zinc-900 dark:bg-white'
                  : 'w-2.5 bg-zinc-400/50 dark:bg-white/40 hover:bg-zinc-500/70 dark:hover:bg-white/70'
                " @click="go(n - 1)" :aria-label="'Go to page ' + n" />
            </div>

            <button @click="next"
              class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200/80 bg-white/90 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
              aria-label="Next page">
              ›
            </button>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const sections = ref([
  {
    id: 'home',
    category: 'Core',
    title: 'Home',
    subtitle: 'Overview of your workspace and quick shortcuts.',
    meta: 'Recent projects, pinned items and updates',
    metaHint: 'Workspace overview',
    short: 'Overview',
    tags: ['Recents', 'Pinned', 'Activity'],
    palette: ['#020617', '#0B1220', '#1F2937', '#4B5563', '#9CA3AF']
  },
  {
    id: 'colors',
    category: 'Design',
    title: 'Colors',
    subtitle: 'Palette and token lab with built-in contrast checker.',
    meta: 'Semantic tokens, tonal ramps, WCAG validation',
    metaHint: 'Color studio',
    short: 'Palettes',
    tags: ['Tokens', 'Palettes', 'WCAG'],
    palette: ['#022C22', '#064E3B', '#10B981', '#6EE7B7', '#ECFDF5']
  },
  {
    id: 'token-studio',
    category: 'Design',
    title: 'Token Studio',
    subtitle: 'Manage semantic and global design tokens in one place.',
    meta: 'Sync, export presets and theme mappings',
    metaHint: 'Tokens',
    short: 'Tokens',
    tags: ['Design Tokens', 'Themes', 'Export'],
    palette: ['#020617', '#1E1B4B', '#312E81', '#6366F1', '#E0E7FF']
  },
  {
    id: 'gradient-craft',
    category: 'Design',
    title: 'Gradient Craft',
    subtitle: 'Generate gradients, presets and multi-stop blends.',
    meta: 'Exports, playgrounds and color harmonies',
    metaHint: 'Gradients',
    short: 'Gradients',
    tags: ['Presets', 'Blend', 'Harmonic'],
    palette: ['#111827', '#1F2937', '#EC4899', '#F97316', '#FEF3C7']
  },
  {
    id: 'sky-studio',
    category: 'Design',
    title: 'Sky Studio',
    subtitle: 'Create vibrant and atmospheric UI color systems.',
    meta: 'Neon, aurora, deep UI tones',
    metaHint: 'Atmospheric color',
    short: 'Sky',
    tags: ['Neon', 'Tone', 'UI Systems'],
    palette: ['#020617', '#0B1120', '#1D4ED8', '#38BDF8', '#E0F2FE']
  },
  {
    id: 'accessibility',
    category: 'Tools',
    title: 'Accessibility Lab',
    subtitle: 'Contrast checker, color blindness simulator and WCAG tools.',
    meta: 'Validation and inclusive color testing',
    metaHint: 'Accessibility',
    short: 'A11y',
    tags: ['WCAG', 'Testing', 'Contrast'],
    palette: ['#020617', '#111827', '#4B5563', '#FACC15', '#F9FAFB']
  },
  {
    id: 'ui-playground',
    category: 'Tools',
    title: 'UI Playground',
    subtitle: 'Preview your palette in real UI components.',
    meta: 'Preview cards, lists, layouts and shells',
    metaHint: 'Preview',
    short: 'Preview',
    tags: ['Components', 'Layouts', 'UI'],
    palette: ['#020617', '#0F172A', '#1E40AF', '#3B82F6', '#DBEAFE']
  },
  {
    id: 'library',
    category: 'Manage',
    title: 'Asset Library',
    subtitle: 'Your saved exports, previews and reference files.',
    meta: 'Centralized assets with folder grouping',
    metaHint: 'Assets',
    short: 'Library',
    tags: ['Exports', 'Files', 'Folders'],
    palette: ['#0B1120', '#020617', '#111827', '#4B5563', '#E5E7EB']
  },
  {
    id: 'support',
    category: 'Help',
    title: 'Support',
    subtitle: 'Get help and talk directly with the team.',
    meta: 'Knowledge base and live assistance',
    metaHint: 'Help center',
    short: 'Support',
    tags: ['Docs', 'Help', 'Contact'],
    palette: ['#020617', '#111827', '#4F46E5', '#8B5CF6', '#EEF2FF']
  }
])

const vw = ref(typeof window !== 'undefined' ? window.innerWidth : 1280)

const perPage = computed(() => {
  if (vw.value >= 1440) return 3
  if (vw.value >= 1024) return 3
  if (vw.value >= 768) return 2
  return 1
})

const pages = computed(() => {
  const res = []
  for (let i = 0; i < sections.value.length; i += perPage.value) {
    res.push(sections.value.slice(i, i + perPage.value))
  }
  return res
})

const totalPages = computed(() => pages.value.length)
const pageIndex = ref(0)

const go = p => {
  pageIndex.value = Math.min(Math.max(p, 0), totalPages.value - 1)
}

const next = () => {
  if (pageIndex.value >= totalPages.value - 1) {
    pageIndex.value = 0
  } else {
    pageIndex.value++
  }
}

const prev = () => {
  if (pageIndex.value <= 0) {
    pageIndex.value = totalPages.value - 1
  } else {
    pageIndex.value--
  }
}

const onResize = () => {
  vw.value = window.innerWidth
  if (pageIndex.value > totalPages.value - 1) {
    pageIndex.value = totalPages.value - 1
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>
